import React, { useState, useEffect } from 'react';
import { adminAPI, brandingAPI } from '../services/api';

/**
 * ADMINISTRATION — L'EN-TÊTE DES DOCUMENTS D'UN DÉPARTEMENT (lot ADM-E, D28 — v78)
 * =================================================================================
 * Spec : doc/modules/admin_panel.md §2.1 · doc/modules/planning_oraux.md §0 D28
 * Maquette : doc/maquettes/admin_maquette.html — ligne « En-tête des documents » de la carte département,
 * modale `#enteteModal` (formulaire + aperçu du haut et du bas d'une convocation).
 *
 * ⚠️ AUCUNE SIGNATURE : le nom et la fonction du signataire sont ÉCRITS ; chaque document porte un QR code de
 * vérification (D28). La source « chef de département de Vie du département » est montrée GRISÉE : elle arrive avec
 * ce module (le serveur la refuse d'ici là).
 * Tant qu'un champ obligatoire manque, l'état de la carte le dit — et les convocations ne partiront pas.
 */

const VIDE = { etablissement: '', adresse: '', telephone: '', courriel: '', ville: '', signataire_nom: '', signataire_fonction: '',
    signataire_source: 'saisi', acces: '', pied: '', logo_universite: true, couleur_filet: '#ea580c' };

// La couleur du trait sous l'en-tête (2026-10-16) — aussi celle des PDF du Planning oraux. Six proposées + une libre.
const PALETTE = [['#ea580c', 'Orange'], ['#1d4ed8', 'Bleu'], ['#15803d', 'Vert'], ['#9f1239', 'Bordeaux'], ['#334155', 'Anthracite'], ['#6d28d9', 'Violet']];

/** Un logo à la taille du PDF (bande de 12 mm de haut, 32 mm de large au plus) ; absent : une case qui dit où le déposer. */
function LogoApercu({ src, libelle, ou }) {
    if (src) return <span className="et-logo-img" title={libelle}><img src={src} alt={libelle} /></span>;
    return <span className="et-logo vide" title={'Aucun logo déposé — ' + ou}>aucun<br />logo</span>;
}

function EtatEntete({ etat }) {
    if (!etat || !etat.configure) return <span className="tag">Non configuré</span>;
    if (!etat.complet) {
        const n = etat.manquants.length;
        return <span className="tag tag-warn" title={'À remplir : ' + etat.manquants.join(', ')}>{n} champ{n > 1 ? 's' : ''} à remplir</span>;
    }
    return <span className="tag tag-ok">Complet</span>;
}

/** La ligne « En-tête des documents » d'une carte département. */
export function EnteteLigneCarte({ etat, onRegler }) {
    return (
        <div className="dc-mail dc-entete">
            <i className="fa-solid fa-file-signature" />
            <span className="dc-mail-libelle">En-tête des documents</span>
            <EtatEntete etat={etat} />
            <span className="grow" />
            <button className="a-btn a-btn-secondary a-btn-sm" onClick={onRegler}>
                <i className="fa-solid fa-gear" /> Régler
            </button>
        </div>
    );
}

/** L'aperçu : le haut et le bas d'une convocation (un champ obligatoire vide apparaît en rouge). */
function Apercu({ f, code, logoDept, logoUniv }) {
    const ou = (v, lib) => (v ? v : <span className="et-vide">{lib}</span>);
    const auj = new Date().toLocaleDateString('fr-FR');
    return (
        <div className="et-apercu">
            <div className="et-page">
                <div className="et-tete" style={{ borderBottomColor: f.couleur_filet }}>
                    {f.logo_universite && <LogoApercu src={logoUniv} libelle="Logo de l'université" ou="à déposer dans Configuration (super-administrateur)" />}
                    <LogoApercu src={logoDept} libelle="Logo du département" ou="à déposer sur la carte du département" />
                    <div className="et-tete-txt">
                        <div className="et-etab">{ou(f.etablissement, 'établissement ?')}</div>
                        <div className="et-coord">{ou(f.adresse, 'adresse ?')}<br />{f.telephone}{f.telephone ? ' · ' : ''}{ou(f.courriel, 'courriel ?')}</div>
                    </div>
                </div>
                <div className="et-lieu">{ou(f.ville, 'ville ?')}, le {auj}</div>
                <div className="et-corps">
                    <b>Objet : Convocation — …</b>
                    <div className="et-ligne" /><div className="et-ligne" style={{ width: '80%' }} /><div className="et-ligne" /><div className="et-ligne" style={{ width: '60%' }} />
                    {f.acces && <div className="et-acces">Accès : {f.acces}</div>}
                </div>
                <div className="et-sign">{ou(f.signataire_fonction, 'fonction ?')}<br /><b>{ou(f.signataire_nom, 'signataire ?')}</b></div>
                <div className="et-pied">
                    <span>{f.pied}</span>
                    <span className="et-ref">Vérifier ce document :<br />Réf. PO-{code}-27-0142<span className="et-qr" /></span>
                </div>
            </div>
            <p className="et-legende">Aperçu — le haut et le bas d'une convocation</p>
        </div>
    );
}

export function EnteteModale({ departement, onFermer, onChange, showMsg }) {
    const [form, setForm] = useState(null);
    const [erreur, setErreur] = useState('');
    const [occupe, setOccupe] = useState(false);
    const [logoUniv, setLogoUniv] = useState(null);

    // le logo de l'université (Configuration) : une absence n'empêche rien, l'aperçu le dit
    useEffect(() => { brandingAPI.get().then((r) => setLogoUniv(r.data?.logo_universite || null)).catch(() => setLogoUniv(null)); }, []);

    useEffect(() => {
        adminAPI.getEntete(departement.id)
            .then((r) => setForm({ ...VIDE, ...r.data.entete }))
            .catch((err) => setErreur(err.response?.data?.error || 'Erreur de lecture de l\'en-tête'));
    }, [departement.id]);

    if (!form) {
        return (
            <div className="admin-modal-overlay" onClick={onFermer}>
                <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="admin-modal-b">{erreur || <><i className="fa-solid fa-spinner fa-spin" /> Chargement…</>}</div>
                </div>
            </div>
        );
    }
    const change = (cle) => (e) => setForm((f) => ({ ...f, [cle]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    const enregistrer = async () => {
        setOccupe(true); setErreur('');
        try {
            const { departement_id, updated_at, modifie_par, ...corps } = form;
            const r = await adminAPI.saveEntete(departement.id, corps);
            showMsg(r.data.message);
            onChange();
            onFermer();
        } catch (err) {
            setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
        } finally { setOccupe(false); }
    };
    const oblig = <span style={{ color: '#dc2626' }}>*</span>;

    return (
        <div className="admin-modal-overlay" onClick={onFermer}>
            <div className="admin-modal et-modale" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="En-tête des documents">
                <div className="admin-modal-h">
                    <i className="fa-solid fa-file-signature" />
                    En-tête des documents — {departement.code}
                    <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
                </div>
                <div className="admin-modal-b admin-page">
                    <p className="hint" style={{ marginBottom: 12 }}>
                        Ces informations figurent sur les <b>documents que l'application fabrique pour ce département</b> — d'abord les{' '}
                        <b>convocations</b> aux oraux (étudiants, maîtres de stage et d'apprentissage). Le <b>logo</b> est celui de la carte du
                        département. Aucune signature n'est apposée : chaque document porte un <b>QR code de vérification</b>.
                    </p>
                    <div className="et-grid">
                        <div>
                            <div className="fg"><label>Établissement et département {oblig}</label><input value={form.etablissement} maxLength={200} onChange={change('etablissement')} placeholder="IUT d'Évry Val d'Essonne — Département QLIO" /><span className="hint">La 1ʳᵉ ligne de l'en-tête.</span></div>
                            <div className="fg"><label>Adresse postale {oblig}</label><input value={form.adresse} maxLength={300} onChange={change('adresse')} /></div>
                            <div className="field-grid">
                                <div className="fg"><label>Téléphone</label><input value={form.telephone} maxLength={40} onChange={change('telephone')} /></div>
                                <div className="fg"><label>Courriel de contact {oblig}</label><input value={form.courriel} maxLength={200} onChange={change('courriel')} /><span className="hint">Pour « prévenez en cas d'empêchement ».</span></div>
                            </div>
                            <div className="fg"><label>Ville {oblig}</label><input value={form.ville} maxLength={100} onChange={change('ville')} /><span className="hint">« {form.ville || 'Évry-Courcouronnes'}, le {new Date().toLocaleDateString('fr-FR')} ».</span></div>
                            <div className="fg"><label>Signataire — source</label>
                                <label className="et-radio"><input type="radio" checked readOnly /> <span>Saisi ici</span></label>
                                <label className="et-radio grise" title="Arrive avec le module Vie du département"><input type="radio" disabled /> <span>Le chef de département de <b>Vie du département</b> (membres &amp; mandatures) <span className="tag">arrive avec le module</span></span></label>
                                <span className="hint">Le jour venu : le nom du membre dont la fonction = Chef de département, <b>à la date d'édition</b> du document ; s'il n'y en a aucun, le nom saisi ici (avec un avertissement).</span>
                            </div>
                            <div className="field-grid">
                                <div className="fg"><label>Signataire — nom {oblig}</label><input value={form.signataire_nom} maxLength={150} onChange={change('signataire_nom')} /></div>
                                <div className="fg"><label>Signataire — fonction {oblig}</label><input value={form.signataire_fonction} maxLength={150} onChange={change('signataire_fonction')} placeholder="Cheffe du département QLIO" /></div>
                            </div>
                            <div className="mail-band info"><i className="fa-solid fa-qrcode" /><div>Le nom et la fonction sont <b>écrits</b> sous le texte ; <b>aucune image de signature</b>. Le QR code mène à une page de vérification : « document authentique, émis par le département le …, pour un oral le … » — <b>aucune donnée personnelle</b>.</div></div>
                            <div className="fg"><label>Accès au site</label><textarea rows={2} value={form.acces} maxLength={500} onChange={change('acces')} /><span className="hint">Bâtiment, parking, accès PMR — repris dans les convocations des maîtres.</span></div>
                            <div className="fg"><label>Pied de page</label><input value={form.pied} maxLength={300} onChange={change('pied')} /></div>
                            <div className="fg"><label>Couleur du trait</label>
                                <div className="et-palette">
                                    {PALETTE.map(([c, nom]) => <button key={c} type="button" className={'et-pastille' + (form.couleur_filet === c ? ' on' : '')} style={{ background: c }} title={nom} aria-label={nom}
                                        onClick={() => setForm((x) => ({ ...x, couleur_filet: c }))} />)}
                                    <label className="et-autre" title="Une autre couleur"><input type="color" value={form.couleur_filet} onChange={(e) => setForm((x) => ({ ...x, couleur_filet: e.target.value }))} /> autre…</label>
                                    <code className="et-code">{form.couleur_filet}</code>
                                </div>
                                <span className="hint">Le trait sous l'en-tête des convocations — et des PDF du Planning oraux (grille, ordre de passage).</span></div>
                            <label className="et-radio"><input type="checkbox" checked={form.logo_universite} onChange={change('logo_universite')} /> <span>Afficher aussi le logo de l'université (à gauche du logo du département)</span></label>
                            <div className="hint et-logos-etat">Logos de l'aperçu : ceux <b>déposés dans l'application</b> — département : {departement.logo_url ? 'déposé (carte du département)' : 'aucun, à déposer sur la carte'} · université : {logoUniv ? 'déposé (Configuration)' : 'aucun'}.</div>
                            {erreur && <div className="mail-band ko" style={{ marginTop: 12 }}><i className="fa-solid fa-triangle-exclamation" /><div>{erreur}</div></div>}
                        </div>
                        <Apercu f={form} code={departement.code} logoDept={departement.logo_url || null} logoUniv={logoUniv} />
                    </div>
                </div>
                <div className="admin-modal-f">
                    <button className="a-btn a-btn-secondary" onClick={onFermer}>Annuler</button>
                    <button className="a-btn a-btn-primary" onClick={enregistrer} disabled={occupe}><i className="fa-solid fa-check" /> Enregistrer</button>
                </div>
            </div>
        </div>
    );
}
